import { CloseOutlined, LogoutOutlined, MenuOutlined, SearchOutlined } from '@ant-design/icons';
import { useEffect, useState } from 'react';
import { useLocation } from 'react-router';

import styled from '@emotion/styled';

import { openCommandMenu } from '../../../components/CommandMenu/CommandMenu';
import HetheraLogo from '../../../components/CompanyLogo/HetheraLogo';
import { ButtonLink, NavBoxItem } from '../../../components/Link/Link';
import useOnLogout from '../../../hooks/auth/useOnLogout';
import { PAGE_GUIDES } from '../../../models/guides/pageGuides';
import { useInbox } from '../../../context/InboxContext';
import { INBOX } from '../../../routes/routeConstants';
import { navGroups } from './navConfig';

// Phones and tablets: a slim top bar (logo, search, menu) and a drawer with
// the same grouped links as the desktop sidebar.
const MobileNav = () => {
  const [open, setOpen] = useState(false);
  const { logoutUser } = useOnLogout();
  const { pathname } = useLocation();
  const { items } = useInbox();

  useEffect(() => {
    setOpen(false);
  }, [pathname]);

  return (
    <>
      <Bar>
        <ButtonLink to='/' aria-label='Hethera home'>
          <HetheraLogo size={24} />
        </ButtonLink>
        <div className='actions'>
          <BarButton onClick={openCommandMenu} aria-label='Search'>
            <SearchOutlined />
          </BarButton>
          <BarButton onClick={() => setOpen(true)} aria-label='Open menu' aria-expanded={open}>
            <MenuOutlined />
          </BarButton>
        </div>
      </Bar>

      <Overlay className={open ? 'open' : ''} onClick={() => setOpen(false)} aria-hidden={!open} />
      <Drawer className={open ? 'open' : ''} aria-label='Main navigation' aria-hidden={!open}>
        <DrawerHead>
          <HetheraLogo size={24} />
          <BarButton onClick={() => setOpen(false)} aria-label='Close menu'>
            <CloseOutlined />
          </BarButton>
        </DrawerHead>

        <Groups>
          {navGroups.map((group, index) => (
            <div key={group.label ?? index} className='group'>
              {group.label && <div className='label'>{group.label}</div>}
              {group.items.map((nav) => (
                <NavBoxItem
                  path={nav.path}
                  key={nav.title}
                  icon={nav.icon}
                  hint={PAGE_GUIDES[nav.path]?.summary}
                  badge={nav.path === INBOX ? items.length : undefined}
                >
                  {nav.title}
                </NavBoxItem>
              ))}
            </div>
          ))}
        </Groups>

        <Logout onClick={logoutUser}>
          <LogoutOutlined /> Sign out
        </Logout>
      </Drawer>
    </>
  );
};

export default MobileNav;

const Bar = styled.header`
  display: none;

  @media (max-width: 884px) {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    z-index: 1500;
    display: flex;
    align-items: center;
    justify-content: space-between;
    height: 56px;
    padding: 0 12px 0 16px;
    background: rgba(251, 250, 247, 0.88);
    backdrop-filter: saturate(1.4) blur(10px);
    border-bottom: 1px solid var(--line);
  }

  & .actions {
    display: flex;
    gap: 4px;
  }
`;

const BarButton = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: transparent;
  color: var(--ink);
  font-size: 17px;
  cursor: pointer;

  &:hover {
    background: var(--hover);
  }
`;

const Overlay = styled.div`
  display: none;

  @media (max-width: 884px) {
    display: block;
    position: fixed;
    inset: 0;
    z-index: 2000;
    background: rgba(14, 26, 19, 0.3);
    opacity: 0;
    pointer-events: none;
    transition: opacity 0.2s ease;

    &.open {
      opacity: 1;
      pointer-events: auto;
    }
  }
`;

const Drawer = styled.nav`
  display: none;

  @media (max-width: 884px) {
    position: fixed;
    top: 0;
    bottom: 0;
    left: 0;
    z-index: 2001;
    display: flex;
    flex-direction: column;
    width: min(300px, 86vw);
    background: var(--panel);
    border-right: 1px solid var(--line);
    box-shadow: var(--shadow-lg);
    transform: translateX(-102%);
    transition: transform 0.25s ease;

    &.open {
      transform: translateX(0);
    }
  }
`;

const DrawerHead = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  height: 56px;
  padding: 0 12px 0 20px;
  border-bottom: 1px solid var(--line);
`;

const Groups = styled.div`
  flex: 1;
  overflow-y: auto;
  padding: 12px 0;
  display: flex;
  flex-direction: column;
  gap: 14px;

  & .group {
    display: flex;
    flex-direction: column;
    gap: 2px;
  }
  & .label {
    padding: 0 20px 4px;
    font-size: 12px;
    font-weight: 500;
    color: #7a8279;
  }
  & a {
    height: 40px;
  }
`;

const Logout = styled.button`
  display: flex;
  align-items: center;
  gap: 10px;
  margin: 0 10px 16px;
  padding: 0 10px;
  height: 40px;
  border: 0;
  border-radius: 9px;
  background: transparent;
  color: var(--muted);
  font-size: 14px;
  cursor: pointer;

  &:hover {
    background: var(--hover);
    color: var(--ink);
  }
`;
